import { Link, useParams, useSearchParams } from "react-router";
import { Badge, EmptyState, Icon, Segmented, useLayout, type Tone } from "@agentsaas/ui";
import type { Actor, PullRequest } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { useSession } from "../app/session";
import { formatRelative } from "../app/format";
import { useAgents } from "../data/queries";
import { usePulls, useTasks } from "../data/collab";
import { Loading } from "../layout/Loading";
import { CollabFrame } from "../project/ProjectChrome";
import { WorkSwitch } from "../project/WorkSwitch";

type StateFilter = "open" | "merged" | "closed" | "all";
const STATES: StateFilter[] = ["open", "merged", "closed", "all"];

export const PR_TONE: Record<PullRequest["state"], Tone> = { open: "accent", merged: "success", closed: "neutral" };

/** 评审结论：最后一个 approve / request_changes 为准 */
export function reviewVerdict(p: PullRequest): "approve" | "request_changes" | null {
  for (let i = p.reviews.length - 1; i >= 0; i--) {
    const e = p.reviews[i]!.event;
    if (e !== "comment") return e;
  }
  return null;
}

export function useActorName(pid: string) {
  const { t } = useT();
  const agents = useAgents(pid);
  const me = useSession().state.user;
  return (a: Actor) =>
    a.type === "agent" ? (agents.data?.find((x) => x.id === a.id)?.name ?? a.id) : a.type === "user" ? (a.id === me?.id ? t("pr.you") : t("task.by_user")) : t("task.by_system");
}

export function PrStateBadge({ pr }: { pr: PullRequest }) {
  const { t } = useT();
  return <Badge tone={PR_TONE[pr.state]}>{t(`pr.state.${pr.state}`)}</Badge>;
}

export function VerdictBadge({ pr }: { pr: PullRequest }) {
  const { t } = useT();
  const v = reviewVerdict(pr);
  if (!v || pr.state !== "open") return null;
  return <Badge tone={v === "approve" ? "success" : "warning"}>{t(`pr.verdict.${v}`)}</Badge>;
}

/** /p/:pid/pulls：项目 PR 列表 */
export function PullsPage() {
  const { pid = "" } = useParams();
  return (
    <CollabFrame pid={pid} section="pulls">
      <PullList pid={pid} />
    </CollabFrame>
  );
}

function PullList({ pid }: { pid: string }) {
  const { t, locale } = useT();
  const mobile = useLayout() === "mobile";
  const [sp, setSp] = useSearchParams();
  const state = (STATES.includes(sp.get("state") as StateFilter) ? sp.get("state") : "open") as StateFilter;
  const pulls = usePulls(pid);
  const tasks = useTasks(pid);
  const who = useActorName(pid);
  const all = pulls.data ?? [];
  const items = all.filter((p) => state === "all" || p.state === state);
  const count = (s: StateFilter) => all.filter((p) => s === "all" || p.state === s).length;
  return (
    <div className={mobile ? "pulls pulls--mobile" : "page pulls"}>
      {mobile ? <WorkSwitch pid={pid} active="pulls" /> : null}
      <div className={mobile ? "tasks__head" : "page__head"}>
        {mobile ? null : <h1 className="page__title">{t("pr.title")}</h1>}
        <Segmented<StateFilter>
          label={t("pr.filter")}
          value={state}
          onChange={(v) => setSp({ state: v }, { replace: true })}
          options={STATES.map((s) => ({ value: s, label: `${t(`pr.filter.${s}`)} ${count(s)}` }))}
        />
      </div>
      {pulls.isPending ? (
        <Loading />
      ) : items.length ? (
        <ul className="list card pr-list" data-testid="pr-list">
          {items.map((p) => {
            const task = tasks.data?.find((x) => x.id === p.taskId);
            return (
              <li key={`${p.repoId}#${p.number}`}>
                <Link to={`/p/${pid}/pulls/${p.repoId}/${p.number}`} className="list-item pr-item" data-testid="pr-item">
                  <Icon name={p.state === "merged" ? "merge" : "pr"} />
                  <span className="grow pr-item__main">
                    <span className="pr-item__title">
                      <strong>{p.title}</strong> <span className="muted">{t("pr.number", { n: p.number })}</span>
                    </span>
                    <span className="small muted pr-item__meta">
                      {who(p.author)}
                      <span className="sep" aria-hidden="true" />
                      <time dateTime={p.updatedAt}>{formatRelative(p.updatedAt, locale)}</time>
                      {task ? (
                        <>
                          <span className="sep" aria-hidden="true" />
                          {t("pr.for_task", { title: task.title })}
                        </>
                      ) : null}
                    </span>
                  </span>
                  <span className="pr-item__side">
                    <span className="diffstat small">
                      <span className="diffstat__add">{t("pr.additions", { n: p.additions })}</span> <span className="diffstat__del">{t("pr.deletions", { n: p.deletions })}</span>
                    </span>
                    <span className="row">
                      <VerdictBadge pr={p} />
                      <PrStateBadge pr={p} />
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="pr" title={t("pr.empty")} />
      )}
    </div>
  );
}
